import { addToWindowRef } from '@avantia/client-and-server-utilities';
import { AnyAction, Dispatch, Middleware } from 'redux';
import { fetchResolver } from '../actions/fetchResolver';
import { HpClientAnswerMap, HpClientQuestionSetState, HpEngineActionTypes, HpGlobalEngineState } from '../types';

export function createPostAnswersMiddleware<
  MiddlewareStateT extends HpPostAnswersMiddlewareState,
  PostDataT extends HpPostAnswersMiddlewarePostData
>({
  websiteEndpoint,
  createBlankState,
  setExtraPostData
}: HpCreatePostAnswersMiddlewareProps<MiddlewareStateT, PostDataT>): Middleware<any, any, Dispatch<AnyAction>> {
  const mwstate: MiddlewareStateT = createBlankState();

  const postAnswersMiddleware: Middleware =
    ({ dispatch, getState }) =>
    (next) =>
    (action) => {
      next(action);
      postAnswers(action, mwstate, websiteEndpoint, dispatch, getState, setExtraPostData);
    };

  return postAnswersMiddleware;
}

function postAnswers<
  MiddlewareStateT extends HpPostAnswersMiddlewareState,
  PostDataT extends HpPostAnswersMiddlewarePostData
>(
  action: any,
  mwstate: MiddlewareStateT,
  websiteEndpoint: string,
  dispatch: Dispatch<AnyAction>,
  getState: () => HpGlobalEngineState,
  setExtraPostData: HpPostAnswersMiddlewareSetExtraPostDataFunction<MiddlewareStateT, PostDataT> | null
) {
  const type = action.type as HpEngineActionTypes;
  if (
    !(
      type === 'DELAYED_FIELD_VALIDATION' ||
      type === 'NAVIGATION_CHANGED' ||
      type === 'FIELD_CHANGED' ||
      type === 'DYNAMIC_BUTTON_CLICK' ||
      type === 'ADDRESS_ITEM_RETRIEVED'
    )
  ) {
    return;
  }

  const { questionSet } = getState();
  const { answers, answersId, productId } = questionSet;

  if (!answersId || answers === mwstate.postedAnswers) {
    return;
  }

  const url = `${websiteEndpoint}/api/answers/${encodeURIComponent(productId)}/${encodeURIComponent(answersId)}`;

  // Provide a hook so Selenium can verify answers, etc.
  addToWindowRef({ answers }, (win) => win.isSeleniumTest === true);

  // Don't use the current thread to submit the answers.
  setTimeout(() => {
    mwstate.postedAnswers = answers;
    fetchResolver({
      options: {
        url,
        method: 'POST',
        data: (setExtraPostData ? setExtraPostData(questionSet, mwstate) : undefined) || { answers },
        description: `Save answers for ID ${answersId}`
      },
      dispatch,
      action: 'ANSWERS_POSTED',
      loadingPageType: 'none'
    });
  }, 1);
}

export interface HpPostAnswersMiddlewareState {
  postedAnswers?: HpClientAnswerMap | undefined;
}

export type HpGetTotalSectionChangesFunc = (
  mwState: HpPostAnswersMiddlewareState,
  incrementedSectionId: string
) => number;

export interface HpPostAnswersMiddlewarePostData {
  answers: HpClientAnswerMap;
}

export type HpPostAnswersMiddlewareSetExtraPostDataFunction<
  MiddlewareStateT extends HpPostAnswersMiddlewareState,
  PostDataT extends HpPostAnswersMiddlewarePostData
> = (questionSet: HpClientQuestionSetState, mwstate: MiddlewareStateT) => PostDataT;

export interface HpCreatePostAnswersMiddlewareProps<
  MiddlewareStateT extends HpPostAnswersMiddlewareState,
  PostDataT extends HpPostAnswersMiddlewarePostData
> {
  websiteEndpoint: string;
  createBlankState: () => MiddlewareStateT;
  setExtraPostData: HpPostAnswersMiddlewareSetExtraPostDataFunction<MiddlewareStateT, PostDataT> | null;
}
